import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { BookOpen, Mic, Star, ArrowRight } from 'lucide-react';

const COURSES = {
  sv: [
    {
      icon: BookOpen, level: 'Nybörjare', name: 'Quran Online Method',
      desc: 'Lär dig läsa Koranen från grunden med en tydlig och pedagogisk metod.',
      points: ['Steg-för-steg inlärning', 'Korrekt uttal från första lektionen', 'Harakat och grundläggande tajweed', 'Läsning av riktiga ord från Koranen'],
    },
    {
      icon: Mic, level: 'Medelnivå', name: 'Koranläsning med Tajweed',
      desc: 'Utveckla din recitation med korrekt tajweed och ökat flyt.',
      points: ['Korrekt uttal av bokstäver och ord', 'Fördjupning i tajweed-regler', 'Flytande läsning av Koranen', 'Praktisk tillämpning i verser'],
    },
    {
      icon: Star, level: 'Avancerad nivå', name: 'Koranmemorering (Hifdh)',
      desc: 'Memorering och fördjupning för elever som vill nå en hög nivå.',
      points: ['Memorering av suror (ḥifẓ)', 'Regelbunden repetition (murājaʿah)', 'Tajweed-korrigering och precision', 'Fördjupning i recitation och kvalitet'],
    },
  ],
  en: [
    {
      icon: BookOpen, level: 'Beginner', name: 'Quran Online Method',
      desc: 'Foundational Arabic reading for beginners.',
      points: ['Arabic letters', 'Harakat (vowel marks)', 'Basic pronunciation', 'Reading simple words'],
    },
    {
      icon: Mic, level: 'Intermediate', name: 'Quran Reading with Tajweed',
      desc: 'Develop your recitation with correct tajweed and increased fluency.',
      points: ['Correct pronunciation of letters and words', 'In-depth tajweed rules', 'Fluent Quran reading', 'Practical application in verses'],
    },
    {
      icon: Star, level: 'Advanced', name: 'Quran Memorization (Hifdh)',
      desc: 'Memorization and depth for students aiming for a high level.',
      points: ['Memorization of surahs (ḥifẓ)', 'Regular revision (murājaʿah)', 'Tajweed correction and precision', 'In-depth recitation and quality'],
    },
  ],
};

const CoursesSection = () => {
  const { language } = useLanguage();
  const sv = language === 'sv';
  const courses = COURSES[language] || COURSES.sv;

  return (
    <section className="py-24" id="courses" data-testid="courses-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Våra kurser' : 'Our Courses'}
          </h2>
          <p className="max-w-xl mx-auto text-sm" style={{ color: 'var(--text-secondary)' }}>
            {sv ? 'Tre nivåer anpassade efter din kunskap och dina mål.' : 'Three levels adapted to your knowledge and goals.'}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {courses.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className={`pricing-card ${i === 1 ? 'featured' : ''}`} data-testid={`course-card-${i}`}>
                <div className="w-14 h-14 rounded-full mb-5 flex items-center justify-center bg-gold/10 border border-gold/20">
                  <Icon size={24} className="text-gold" />
                </div>
                <p className="text-xs text-gold font-medium tracking-widest uppercase mb-1">{c.level}</p>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{c.name}</h3>
                <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>{c.desc}</p>
                <ul className="space-y-3 mb-8">
                  {c.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 flex-shrink-0"></span>
                      {p}
                    </li>
                  ))}
                </ul>
                <Link to="/booking" className="btn-gold w-full inline-flex items-center justify-center gap-2 text-sm" data-testid={`apply-course-${i}`}>
                  {sv ? 'Boka gratis provlektion' : 'Book Free Trial'} <ArrowRight size={16} />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CoursesSection;
